import '../styles/nav.css';
import type { SectionId } from '../scenes/controller';
import { getDevPanel } from '../dev/devPanel';

export type NavVariant = 'horizontal' | 'hamburger' | 'bottom-tabs';

const LINKS: { label: string; section: SectionId; href: string }[] = [
  { label: 'Home', section: 'home', href: '#/' },
  { label: 'Gallery', section: 'gallery', href: '#/gallery' },
  { label: 'Contact', section: 'contact', href: '#/contact' },
];

function hashToSection(hash: string): SectionId {
  if (hash.startsWith('#/gallery')) return 'gallery';
  if (hash === '#/contact') return 'contact';
  return 'home';
}

export function mountNav(initialVariant: NavVariant = 'horizontal'): () => void {
  const nav = document.createElement('nav');
  nav.id = 'sitenav';
  nav.className = 'site-nav';
  nav.setAttribute('aria-label', 'Main navigation');
  nav.dataset['variant'] = initialVariant;

  const linksHtml = LINKS.map(
    l =>
      `<li><a href="${l.href}" class="nav-link" data-section="${l.section}">${l.label}</a></li>`,
  ).join('');

  nav.innerHTML = `
    <div class="nav-inner">
      <a class="nav-brand" href="#/">IEDLabs</a>
      <button class="nav-hamburger" aria-label="Toggle navigation" aria-expanded="false" aria-controls="nav-links">
        <span></span><span></span><span></span>
      </button>
      <ul id="nav-links" class="nav-links" role="list">${linksHtml}</ul>
    </div>
  `;

  const app = document.getElementById('app');
  app?.before(nav);

  document.body.dataset['navVariant'] = initialVariant;

  function updateActive(): void {
    const current = hashToSection(location.hash);
    nav.querySelectorAll<HTMLAnchorElement>('.nav-link').forEach(a => {
      if (a.dataset['section'] === current) {
        a.setAttribute('aria-current', 'page');
      } else {
        a.removeAttribute('aria-current');
      }
    });
  }

  const hamburger = nav.querySelector<HTMLButtonElement>('.nav-hamburger')!;

  hamburger.addEventListener('click', () => {
    const open = nav.hasAttribute('data-open');
    nav.toggleAttribute('data-open', !open);
    hamburger.setAttribute('aria-expanded', String(!open));
  });

  nav.querySelectorAll('.nav-link').forEach(a => {
    a.addEventListener('click', () => {
      nav.removeAttribute('data-open');
      hamburger.setAttribute('aria-expanded', 'false');
    });
  });

  window.addEventListener('hashchange', updateActive);
  updateActive();

  let destroyGui: (() => void) | null = null;

  const panel = getDevPanel();
  if (panel) {
    const folder = panel.addFolder('Navigation');
    const config = { variant: initialVariant };
    folder
      .add(config, 'variant', ['horizontal', 'hamburger', 'bottom-tabs'])
      .name('Variant')
      .onChange((v: NavVariant) => {
        nav.dataset['variant'] = v;
        document.body.dataset['navVariant'] = v;
        nav.removeAttribute('data-open');
        hamburger.setAttribute('aria-expanded', 'false');
      });
    folder.close();
    destroyGui = (): void => folder.destroy();
  }

  return () => {
    window.removeEventListener('hashchange', updateActive);
    destroyGui?.();
    nav.remove();
  };
}
